import { TaskStatus } from '@bariendo/lib/constants';
import { TASK_COMPLETED_BY_EXTENSION } from '@bariendo/lib/extensions';
import { Button, Group, Text } from '@mantine/core';
import { createReference, PatchOperation } from '@medplum/core';
import { Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback } from 'react';
import { alert } from '@/services/alert';
import { useGuardedTaskPatch } from './useGuardedTaskPatch';

const UNDO_TOAST_AUTO_CLOSE_MS = 8000;

/**
 * Completes (and un-completes) clinical tasks via the guarded patch
 * protocol (see useGuardedTaskPatch).
 *
 * `completed` here is the plain FHIR status — safe because every clinical-task
 * query is scoped by the orbit-provider-task group identifier, so the inbox's
 * "completed = archived" reading of notification Tasks never sees these (see
 * @bariendo/lib/clinical-tasks).
 */
export function useTaskCompletion(onChange?: () => void | Promise<void>) {
  const medplum = useMedplum();
  const { patchTask, isUpdating } = useGuardedTaskPatch(onChange);

  const patchStatus = useCallback(
    (task: Task, status: TaskStatus, extraOps: PatchOperation[] = []): Promise<Task | undefined> =>
      patchTask(task, [{ op: 'replace', path: '/status', value: status }, ...extraOps]),
    [patchTask]
  );

  /**
   * Ops stamping WHO completed the task (task-completed-by extension, read
   * by the admin task log). Other extensions are carried through untouched.
   */
  const buildCompletedByOps = useCallback(
    (task: Task): PatchOperation[] => {
      const profile = medplum.getProfile();
      if (!profile) {
        return [];
      }
      // Append, don't replace: any other extension on the task must survive.
      const others = (task.extension ?? []).filter((e) => e.url !== TASK_COMPLETED_BY_EXTENSION);
      return [
        {
          op: task.extension !== undefined ? 'replace' : 'add',
          path: '/extension',
          value: [...others, { url: TASK_COMPLETED_BY_EXTENSION, valueReference: createReference(profile) }],
        },
      ];
    },
    [medplum]
  );

  /**
   * Reverts a completion; used by the Undo action on the toast.
   * `restoreStatus` is the task's pre-completion status, so undoing an
   * in-progress task doesn't demote it to ready.
   */
  const reopenTask = useCallback(
    async (task: Task, restoreStatus: TaskStatus = TaskStatus.READY): Promise<void> => {
      // Un-completing also clears the completed-by stamp.
      const remaining = (task.extension ?? []).filter((e) => e.url !== TASK_COMPLETED_BY_EXTENSION);
      const clearOps: PatchOperation[] =
        task.extension === undefined
          ? []
          : remaining.length > 0
            ? [{ op: 'replace', path: '/extension', value: remaining }]
            : [{ op: 'remove', path: '/extension' }];
      const reopened = await patchStatus(task, restoreStatus, clearOps);
      if (reopened) {
        alert.success({ title: 'Task reopened', message: reopened.description ?? '' });
      }
    },
    [patchStatus]
  );

  /** Toast with an Undo action that restores the task's pre-transition status. */
  const showUndoToast = useCallback(
    (title: string, updated: Task, previousStatus: TaskStatus): void => {
      const toastId = alert.success({
        title,
        options: { autoClose: UNDO_TOAST_AUTO_CLOSE_MS },
        message: (
          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Text size="sm" lineClamp={1}>
              {updated.description}
            </Text>
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => {
                alert.hide(toastId);
                reopenTask(updated, previousStatus).catch(() => undefined);
              }}
            >
              Undo
            </Button>
          </Group>
        ),
      });
    },
    [reopenTask]
  );

  const completeTask = useCallback(
    async (task: Task): Promise<void> => {
      const previousStatus = (task.status as TaskStatus) ?? TaskStatus.READY;
      const completed = await patchStatus(task, TaskStatus.COMPLETED, buildCompletedByOps(task));
      if (completed) {
        showUndoToast('Task completed', completed, previousStatus);
      }
    },
    [buildCompletedByOps, patchStatus, showUndoToast]
  );

  /**
   * Soft delete: cancels rather than deletes, keeping the audit trail (see
   * @bariendo/lib/clinical-tasks). Callers confirm first — this runs the
   * write. The Undo on the toast restores the pre-delete status.
   */
  const deleteTask = useCallback(
    async (task: Task): Promise<void> => {
      const previousStatus = (task.status as TaskStatus) ?? TaskStatus.READY;
      const cancelled = await patchStatus(task, TaskStatus.CANCELLED);
      if (cancelled) {
        showUndoToast('Task deleted', cancelled, previousStatus);
      }
    },
    [patchStatus, showUndoToast]
  );

  return { completeTask, deleteTask, reopenTask, isUpdating };
}
